/**
 * Edit my farm — the signed-in farmer's self-service farm-profile editor (web).
 *
 * D-021 Path A. Web counterpart of apps/mobile/app/farm-edit.tsx. Reached from
 * _app.my-farm.tsx ("Edit farm details"). Loads the caller's own farm via
 * GET /me/farm-profile (reusing the read screen's query key ['me-farm-profile']
 * so a Save primes the same cache) and lets them change tree count, estimated
 * capacity, and farm GPS, persisted via PATCH /me/farm-profile on an explicit
 * Save.
 *
 * Field rules mirror the G1 self-signup capture + the mobile editor:
 *   - treeCount: required, whole number >= 0.
 *   - estCapacityKg: OPTIONAL, whole number >= 0; blank clears it to null.
 *   - farmGps: OPTIONAL lat/lng, ALL-OR-NOTHING (both or neither). "Use current
 *     location" fills it via navigator.geolocation; manual entry is allowed;
 *     "Clear GPS" / blanking both sends farmGps: null.
 * Decimals on the whole-number fields are rejected with a clear helper message.
 *
 * Only fields that actually changed from the loaded profile are sent. An
 * unchanged Save is a no-op success (we don't call the API / trip the server's
 * empty-patch 400). On success: prime the cache then navigate back to /my-farm.
 *
 * Contract verified against services/api/src/me/dto.ts via the manual client
 * src/api/manual/me-farm.ts (validate-API-before-smoke rule).
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Stack,
  Text,
  TextField,
} from '@fse/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FseApiError } from '../api/client';
import { meControllerGetFarmProfile, meControllerUpdateFarmProfile } from '../api/generated/me/me';
import type {
  MyFarmGpsDto,
  MyFarmProfileDto,
  UpdateFarmProfileResultDto,
  UpdateMyFarmProfileDto,
} from '../api/generated/models';
import { RegionSelect } from '../components/RegionSelect';
import { normalizeRegion, type SaProvince } from '../lib/regions';

/** A whole number >= 0, or null for "not set". Decimals/negatives are invalid. */
function parseWholeNonNeg(raw: string): { ok: boolean; value: number | null } {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { ok: true, value: null };
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < 0) return { ok: false, value: null };
  return { ok: true, value: n };
}

/** A finite number, or null for "not set". */
function parseFinite(raw: string): { ok: boolean; value: number | null } {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { ok: true, value: null };
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return { ok: false, value: null };
  return { ok: true, value: n };
}

/**
 * Read the farmer's own coarse region off the farm profile DEFENSIVELY. The
 * backend lane adds `region: string | null` to GET /me/farm-profile, but orval
 * hasn't regenerated the type yet (post-deploy regen) — so we cast at the
 * boundary (same pattern as the marketplace card) and normalise to a canonical
 * province or null. Once the type lands this can read `farm.region`.
 */
function readFarmRegion(farm: MyFarmProfileDto): SaProvince | null {
  return normalizeRegion((farm as { region?: string | null }).region);
}

/** Shallow-equal two {lat,lng} pairs (or nulls). */
function gpsEqual(a: MyFarmGpsDto | null, b: MyFarmGpsDto | null): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return a.lat === b.lat && a.lng === b.lng;
}

export function MyFarmEditScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const farmQuery = useQuery({
    // Same key as the read screen (_app.my-farm.tsx) so a Save primes the cache
    // that screen renders — the return trip shows the saved values immediately.
    queryKey: ['me-farm-profile'],
    queryFn: ({ signal }) => meControllerGetFarmProfile(signal),
    retry: (failureCount, error) =>
      error instanceof FseApiError && error.status === 404 ? false : failureCount < 3,
  });

  // Editable fields, held as strings for the inputs. Seeded from the loaded
  // profile once; `seeded` guards against re-clobbering edits on a background
  // refetch (mirrors _app.profile.tsx).
  const [treeCount, setTreeCount] = useState('');
  const [estCapacityKg, setEstCapacityKg] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [region, setRegion] = useState<SaProvince | null>(null);
  const [seeded, setSeeded] = useState(false);

  const [showErrors, setShowErrors] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState<string | null>(null);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);

  const farm = farmQuery.data;

  useEffect(() => {
    if (!seeded && farm) {
      setTreeCount(String(farm.treeCount));
      setEstCapacityKg(farm.estCapacityKg == null ? '' : String(farm.estCapacityKg));
      setLat(farm.farmGps == null ? '' : String(farm.farmGps.lat));
      setLng(farm.farmGps == null ? '' : String(farm.farmGps.lng));
      setRegion(readFarmRegion(farm));
      setSeeded(true);
    }
  }, [seeded, farm]);

  const saveMutation = useMutation({
    mutationFn: (patch: UpdateMyFarmProfileDto) => meControllerUpdateFarmProfile(patch),
    onSuccess: (result: UpdateFarmProfileResultDto) => {
      // Prime the cache the read screen uses with the returned profile (unchanged
      // when the edit was routed to review rather than applied).
      queryClient.setQueryData(['me-farm-profile'], result.profile);
      if (result.reviewPending) {
        // Coop/group member: the edit was queued for the coop_admin / group_lead
        // to approve, NOT applied. Surface that, then return to the read screen.
        setReviewSubmitted(true);
        window.setTimeout(() => navigate({ to: '/my-farm' }), 1600);
      } else {
        navigate({ to: '/my-farm' });
      }
    },
  });

  function useCurrentLocation() {
    setGpsError(null);
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setGpsError(t('myFarmEdit.gps.unavailable'));
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(String(pos.coords.latitude));
        setLng(String(pos.coords.longitude));
        if (showErrors) setShowErrors(false);
        setGpsLoading(false);
      },
      (err) => {
        setGpsError(
          err.code === err.PERMISSION_DENIED
            ? t('myFarmEdit.gps.permissionDenied')
            : t('myFarmEdit.gps.locateError', { error: err.message }),
        );
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 15_000 },
    );
  }

  function clearGps() {
    setLat('');
    setLng('');
    setGpsError(null);
    if (showErrors) setShowErrors(false);
  }

  // ---------- per-field validation (drives helper text / error state) ----------
  const treeParsed = parseWholeNonNeg(treeCount);
  const treeInvalid = !treeParsed.ok || treeParsed.value == null;
  const capParsed = parseWholeNonNeg(estCapacityKg);
  const capInvalid = !capParsed.ok;
  const latParsed = parseFinite(lat);
  const lngParsed = parseFinite(lng);
  // GPS is all-or-nothing: both or neither, and each must parse to a finite num.
  const gpsBothSet = latParsed.value != null && lngParsed.value != null;
  const gpsNeitherSet = latParsed.value == null && lngParsed.value == null;
  const gpsCoordsInvalid = !latParsed.ok || !lngParsed.ok;
  const gpsPartial = !gpsBothSet && !gpsNeitherSet;
  const gpsInvalid = gpsCoordsInvalid || gpsPartial;

  const isSaving = saveMutation.isPending;
  const treeShowError = showErrors && treeInvalid;
  const capShowError = showErrors && capInvalid;
  const gpsShowError = showErrors && gpsInvalid;

  function onSave() {
    setShowErrors(true);
    if (treeInvalid || capInvalid || gpsInvalid) return;
    if (!farm) return;

    const nextGps: MyFarmGpsDto | null = gpsBothSet
      ? { lat: latParsed.value as number, lng: lngParsed.value as number }
      : null;

    // Only send fields that actually changed from the loaded profile. `region`
    // is carried defensively (orval hasn't regenerated UpdateMyFarmProfileDto
    // for it yet — PATCH /me/farm-profile already accepts `{ region }`).
    const patch: UpdateMyFarmProfileDto & { region?: SaProvince | null } = {};
    if (treeParsed.value !== farm.treeCount) {
      patch.treeCount = treeParsed.value as number;
    }
    if (capParsed.value !== farm.estCapacityKg) {
      patch.estCapacityKg = capParsed.value;
    }
    if (!gpsEqual(nextGps, farm.farmGps)) {
      patch.farmGps = nextGps;
    }
    if (region !== readFarmRegion(farm)) {
      patch.region = region; // SaProvince to set, null to clear
    }

    // Nothing changed server-side — treat Save as a no-op success rather than
    // trip the server's empty-patch 400.
    if (
      patch.treeCount === undefined &&
      patch.estCapacityKg === undefined &&
      patch.farmGps === undefined &&
      patch.region === undefined
    ) {
      navigate({ to: '/my-farm' });
      return;
    }

    saveMutation.mutate(patch);
  }

  const isNotFarmer = farmQuery.error instanceof FseApiError && farmQuery.error.status === 404;

  return (
    <Stack spacing={3} sx={{ maxWidth: 640 }}>
      <Box>
        <Text variant="headline-small" as="h2">
          {t('myFarmEdit.title')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {t('myFarmEdit.subtitle')}
          </Text>
        </Box>
      </Box>

      <Box>
        <Link to="/my-farm" style={{ textDecoration: 'none' }}>
          <Button variant="text" size="small">
            {t('myFarmEdit.back')}
          </Button>
        </Link>
      </Box>

      {reviewSubmitted && (
        <Alert severity="info">{t('myFarmEdit.submittedForReview')}</Alert>
      )}

      {farmQuery.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : isNotFarmer ? (
        <Card>
          <CardHeader title={t('myFarmEdit.notFarmer.title')} />
          <CardContent>
            <Text variant="body-medium" color="text.secondary">
              {t('myFarmEdit.notFarmer.body')}
            </Text>
          </CardContent>
        </Card>
      ) : farmQuery.isError || !farm ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => farmQuery.refetch()}>
              {t('myFarmEdit.retry')}
            </Button>
          }
        >
          {t('myFarmEdit.loadError', { error: String(farmQuery.error) })}
        </Alert>
      ) : (
        <Card>
          <CardHeader title={t('myFarmEdit.farmDetails')} />
          <CardContent>
            <Stack spacing={3}>
              {/* Tree count — required whole number >= 0 */}
              <TextField
                label={t('myFarmEdit.field.treeCount.label')}
                value={treeCount}
                onChange={(e) => {
                  setTreeCount(e.target.value);
                  if (showErrors) setShowErrors(false);
                }}
                type="number"
                slotProps={{ htmlInput: { min: 0, step: 1, inputMode: 'numeric' } }}
                error={treeShowError}
                helperText={
                  treeShowError
                    ? t('myFarmEdit.field.treeCount.error')
                    : t('myFarmEdit.field.treeCount.hint')
                }
                disabled={isSaving}
                fullWidth
              />

              {/* Estimated capacity — optional whole number, clearable */}
              <TextField
                label={t('myFarmEdit.field.estCapacity.label')}
                value={estCapacityKg}
                onChange={(e) => {
                  setEstCapacityKg(e.target.value);
                  if (showErrors) setShowErrors(false);
                }}
                type="number"
                slotProps={{ htmlInput: { min: 0, step: 1, inputMode: 'numeric' } }}
                error={capShowError}
                helperText={
                  capShowError
                    ? t('myFarmEdit.field.estCapacity.error')
                    : t('myFarmEdit.field.estCapacity.hint')
                }
                disabled={isSaving}
                fullWidth
              />

              {/* Region — optional SA province (geo-region MVP). Buyers see this
                  as the seller location on marketplace cards + use it for a
                  "near me" sort. Coarse on purpose; the precise GPS stays private. */}
              <Box>
                <RegionSelect
                  label={t('myFarmEdit.field.region.label', 'Region')}
                  blankLabel={t('myFarmEdit.field.region.none', 'Not set')}
                  value={region}
                  onChange={setRegion}
                  disabled={isSaving}
                />
                <Box sx={{ display: 'block', mt: 1 }}>
                  <Text variant="body-small" color="text.secondary">
                    {t(
                      'myFarmEdit.field.region.hint',
                      'Your province. Shown to buyers on your listings; helps them find produce near them.',
                    )}
                  </Text>
                </Box>
              </Box>

              {/* Farm GPS — optional, all-or-nothing, capture or manual */}
              <Box>
                <Box
                  sx={{
                    display: 'block',
                    mb: 1,
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                  }}
                >
                  <Text variant="label-medium" color="text.secondary">
                    {t('myFarmEdit.field.gps.heading')}
                  </Text>
                </Box>

                <Stack direction="row" spacing={2}>
                  <TextField
                    label={t('myFarmEdit.field.gps.latitude')}
                    value={lat}
                    onChange={(e) => {
                      setLat(e.target.value);
                      if (showErrors) setShowErrors(false);
                    }}
                    type="number"
                    slotProps={{ htmlInput: { step: 'any', inputMode: 'decimal' } }}
                    error={gpsShowError}
                    disabled={isSaving}
                    fullWidth
                  />
                  <TextField
                    label={t('myFarmEdit.field.gps.longitude')}
                    value={lng}
                    onChange={(e) => {
                      setLng(e.target.value);
                      if (showErrors) setShowErrors(false);
                    }}
                    type="number"
                    slotProps={{ htmlInput: { step: 'any', inputMode: 'decimal' } }}
                    error={gpsShowError}
                    disabled={isSaving}
                    fullWidth
                  />
                </Stack>

                <Box sx={{ display: 'block', mt: 1 }}>
                  <Text
                    variant="body-small"
                    color={gpsShowError ? 'error.main' : 'text.secondary'}
                  >
                    {gpsShowError
                      ? t('myFarmEdit.field.gps.error')
                      : t('myFarmEdit.field.gps.hint')}
                  </Text>
                </Box>

                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={useCurrentLocation}
                    disabled={isSaving || gpsLoading}
                  >
                    {gpsLoading ? t('myFarmEdit.field.gps.locating') : t('myFarmEdit.field.gps.useCurrent')}
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    onClick={clearGps}
                    disabled={isSaving || gpsLoading || (lat.trim() === '' && lng.trim() === '')}
                  >
                    {t('myFarmEdit.field.gps.clear')}
                  </Button>
                </Stack>

                {gpsError ? (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {gpsError}
                  </Alert>
                ) : null}
              </Box>

              {saveMutation.isError ? (
                <Alert severity="error">
                  {t('myFarmEdit.saveError', { error: String(saveMutation.error) })}
                </Alert>
              ) : null}

              <Box>
                <Button variant="contained" onClick={onSave} disabled={isSaving}>
                  {isSaving ? t('myFarmEdit.saving') : t('myFarmEdit.save')}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}
